import React, {useCallback} from 'react'
import {COMPANY_OWNERSHIP_STRUCTURE, COMPANY_REVENUE_GROUP} from '@modifi/plugin-onboarding'
import {Form} from '@modifi/plugin-forms'
import {Caption, Col, DangerAlert, H5, Row} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {OwnershipStructure} from '@modifi/enums'

import {EntityFormData} from '../../../../types/common'
import CompanyFinancialsFormContent from './company-financials-form-content'
import CompanyFormFooter from '../company-form-footer'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchCompany} from '../../../../data/company'

export type CompanyFinancialsProps = {
  onSubmit: (changes: Record<string, unknown>) => void
  onDiscard: () => void
  saveErrorMessage?: string
  savePending: boolean
  redirectToCompanyIntroduction: () => void
  formData: EntityFormData
  redirectToBuyerDetails: () => void
}

const CompanyFinancials: React.FC<CompanyFinancialsProps> = ({
  onSubmit,
  onDiscard,
  saveErrorMessage = '',
  savePending,
  redirectToCompanyIntroduction,
  formData,
  redirectToBuyerDetails,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])

  const handleSubmit = useCallback(
    (changes: Record<string, unknown>) => {
      const dataOnSelection = changes

      if (
        !changes?.[COMPANY_OWNERSHIP_STRUCTURE] &&
        !formData.initialValues?.[COMPANY_OWNERSHIP_STRUCTURE]
      ) {
        dataOnSelection[COMPANY_OWNERSHIP_STRUCTURE] = OwnershipStructure.SINGLE_ENTITY
      }
      if (changes?.[COMPANY_OWNERSHIP_STRUCTURE] === OwnershipStructure.SINGLE_ENTITY) {
        delete dataOnSelection[COMPANY_REVENUE_GROUP]
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'intro_step_2_submit',
        element_id: 'intro_step_2_submit',
        element_type: 'button',
      })

      onSubmit(dataOnSelection)
    },
    [formData.initialValues, onSubmit]
  )

  const handleBackButtonClick = useCallback(() => {
    if (!saveErrorMessage) {
      onDiscard()
      redirectToCompanyIntroduction()
    }
  }, [onDiscard, redirectToCompanyIntroduction, saveErrorMessage])

  const handleForwardButtonClick = useCallback(() => {
    if (!saveErrorMessage) {
      onDiscard()
      redirectToBuyerDetails()
    }
  }, [onDiscard, redirectToBuyerDetails, saveErrorMessage])

  return (
    <>
      <Row>
        <Col>
          {/* @todo_pre_underwriting using t function for translation is giving error in console:
            Cannot update a component (`ProgressSteps`) while rendering a different component
            (`CompanyFinancials`). To locate the bad setState() call inside `CompanyFinancials` */}
          <H5>{t('intro-page.company-financials.headline')}</H5>
        </Col>
      </Row>
      <Row mb={24}>
        <Col>
          <Caption>{t('intro-page.company-financials.description')}</Caption>
        </Col>
      </Row>
      {saveErrorMessage && (
        <Row>
          <Col>
            <DangerAlert>{saveErrorMessage}</DangerAlert>
          </Col>
        </Row>
      )}
      <Form
        isLoading={savePending}
        defaultValues={formData?.defaultValues}
        initialValues={formData?.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <CompanyFinancialsFormContent />
        <CompanyUnlockRefresher
          refetch={(): any[] => [aFetchCompany()]}
          initialValues={formData?.initialValues}
          defaultValues={formData?.defaultValues}
        />
        <CompanyFormFooter
          hasError={!!saveErrorMessage}
          loading={savePending}
          canMoveBack
          handleBackButtonClick={handleBackButtonClick}
          handleForwardButtonClick={handleForwardButtonClick}
          dataTargetSuffix="from-step-2"
        />
      </Form>
    </>
  )
}

export default CompanyFinancials
